import _extends from "@babel/runtime/helpers/extends";
import React, { memo, useMemo, Fragment, useCallback } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import Text from '../../../typography/Text';
import Link from '../../../typography/Link';
import Icon from '../../../general/Icon';
import { useClasses } from '../../../../utils/overrides';
import { createUseStyles, useTheme } from '../../../../utils/styles';
import { Transition } from '../transitions';
import { TYPES } from '../constants';
import styles from './styles';
const useStyles = createUseStyles(styles, 'Toast');
const Toast = /*#__PURE__*/memo(_ref => {
  let {
    classes: classesProp,
    className: classNameProp,
    override,
    id,
    type,
    onClick,
    closeOnClick,
    closeButton,
    useDefaultCloseButton,
    onClose,
    title,
    text,
    icon,
    isActive,
    clearDeletedToast,
    onClickLink,
    linkText,
    transition = 'slide',
    content = null
  } = _ref;
  const theme = useTheme();
  const classes = useClasses(useStyles, classesProp);
  const rootClassName = classnames(classes.root, {
    [classes[TYPES.success]]: !content && type === TYPES.success,
    [classes[TYPES.warning]]: !content && type === TYPES.warning,
    [classes[TYPES.error]]: !content && type === TYPES.error,
    [classes[TYPES.info]]: !content && type === TYPES.info
  }, classNameProp);
  const handleOnClose = useCallback(e => {
    e.stopPropagation();
    onClose();
  }, [onClose]);
  const handleOnClick = useCallback(() => {
    if (onClick) onClick();
    if (!closeOnClick) return;
    onClose();
  }, [closeOnClick, onClose, onClick]);
  const handleOnClickLink = useCallback(e => {
    e.stopPropagation();
    onClickLink();
  }, [onClickLink]);
  const toastContent = useMemo(() => {
    return /*#__PURE__*/React.createElement(Fragment, null, /*#__PURE__*/React.createElement("div", {
      className: classes.header
    }, /*#__PURE__*/React.createElement(Text, {
      className: classes.title,
      type: "subtitle",
      bold: true
    }, title), closeButton && closeButton, !closeButton && useDefaultCloseButton && /*#__PURE__*/React.createElement(Icon, {
      className: classes.close,
      name: "close",
      size: "large",
      onClick: handleOnClose
    })), !text && content, !content && /*#__PURE__*/React.createElement("div", {
      className: classes.content
    }, /*#__PURE__*/React.createElement(Text, {
      className: classes.text,
      type: "caption",
      color: theme.colors.utility.textSecondary
    }, text), onClickLink && linkText && /*#__PURE__*/React.createElement(Link, {
      className: classes.link,
      type: "caption",
      onClick: handleOnClickLink
    }, linkText)));
  }, [classes, content, text, title, handleOnClose, closeButton, useDefaultCloseButton, onClickLink, handleOnClickLink, linkText, theme]);
  const iconType = useMemo(() => {
    const icons = {
      [TYPES.success]: {
        name: 'taskChecked',
        color: theme.colors.semantic.positive500
      },
      [TYPES.warning]: {
        name: 'warning',
        color: theme.colors.semantic.focus500
      },
      [TYPES.error]: {
        name: 'error',
        color: theme.colors.semantic.negative500
      },
      [TYPES.info]: {
        name: 'info',
        color: theme.colors.semantic.info500
      }
    };
    if (!icons[type]) return null;
    return /*#__PURE__*/React.createElement(Icon, {
      name: icons[type].name,
      color: icons[type].color
    });
  }, [type, theme]);
  const toastWrapperClassName = classnames(classes.toastWrapper, {
    [classes.withIcon]: !!iconType || !!icon
  });
  return /*#__PURE__*/React.createElement(Transition, {
    transition: transition,
    transitionKey: id,
    show: isActive,
    timeout: 300,
    onExited: () => clearDeletedToast(id)
  }, /*#__PURE__*/React.createElement("div", _extends({
    className: rootClassName
  }, override.Toast, {
    onClick: handleOnClick
  }), (iconType || icon) && /*#__PURE__*/React.createElement("div", _extends({
    className: toastWrapperClassName
  }, override.ToastWrapper), /*#__PURE__*/React.createElement("div", {
    className: classes.iconBox
  }, iconType || icon), /*#__PURE__*/React.createElement("div", {
    className: classes.contentBox
  }, toastContent)), !iconType && !content && !icon && /*#__PURE__*/React.createElement("div", _extends({
    className: toastWrapperClassName
  }, override.ToastWrapper), toastContent), !title && !icon && !iconType && content));
});
process.env.NODE_ENV !== "production" ? Toast.propTypes = {
  /** Custom className to apply to the toast */
  className: PropTypes.string,
  /** Override the styles of any part of the component */
  overrides: PropTypes.object,
  /** Unique identifier for this specific toast instance */
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** Container ID where this toast will be rendered */
  containerId: PropTypes.any,
  /** Custom React element to display as the toast content, replacing the default layout */
  content: PropTypes.element,
  /** Main message text of the toast */
  text: PropTypes.string,
  /** Header text displayed at the top of the toast */
  title: PropTypes.string,
  /** Toast notification type, affects styling and icon */
  type: PropTypes.oneOf(['success', 'warning', 'error', 'info']),
  /** Custom icon to display instead of the default type icon */
  icon: PropTypes.node,
  /** Custom close button element to replace the default one */
  closeButton: PropTypes.element,
  /** Custom className for the close button */
  closeButtonClassName: PropTypes.string,
  /** When true, clicking on the toast will dismiss it */
  closeOnClick: PropTypes.bool,
  /** Determines toast ordering in the container. When true, new toasts appear at the top */
  newestOnTop: PropTypes.bool,
  /** Controls automatic dismissal. False disables auto-close, a number sets the duration in milliseconds */
  autoClose: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
  /** When true, displays the default close button. When false, no close button is shown unless closeButton prop is provided */
  useDefaultCloseButton: PropTypes.bool,
  /** Function called when the toast is clicked */
  onClick: PropTypes.func,
  /** Function called when the toast is closed */
  onClose: PropTypes.func,
  /** Function called when the link in the toast is clicked */
  onClickLink: PropTypes.func,
  /** Text for the clickable link that can be displayed in the toast */
  linkText: PropTypes.string,
  /** Animation type for toast appearance/disappearance */
  transition: PropTypes.oneOf(['slide', 'fade']),
  /** Whether the toast is currently visible */
  isActive: PropTypes.bool,
  /** Function to clear toast after transition */
  clearDeletedToast: PropTypes.func
} : void 0;
export default Toast;